export type Hsva = { h: number; s: number; v: number; a: number }; // h is 0 to 360. s, v are 0 to 100. a is 0 to 255.
export type Rgba = { r: number; g: number; b: number; a: number }; // r, g, b, a are all 0 to 255.

function clamp(n: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, n));
}

function toHex2(n: number): string {
	return clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
}

// Opaque black is the safe fallback when a value cannot be parsed.
const DEFAULT_HSVA: Hsva = { h: 0, s: 0, v: 0, a: 255 };

/** Parse #RGB, #RRGGBB, #RRGGBBAA, or the "transparent" keyword into RGBA. Returns null when it cannot parse. */
export function parseToRgba(input: string | undefined): Rgba | null {
	const raw = (input ?? "").trim().toLowerCase();
	if (raw === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
	const hex = raw.startsWith("#") ? raw.slice(1) : raw;
	if (/^[0-9a-f]{3}$/.test(hex)) {
		const r = Number.parseInt(hex[0] + hex[0], 16);
		const g = Number.parseInt(hex[1] + hex[1], 16);
		const b = Number.parseInt(hex[2] + hex[2], 16);
		return { r, g, b, a: 255 };
	}
	if (/^[0-9a-f]{6}$/.test(hex)) {
		return { r: Number.parseInt(hex.slice(0, 2), 16), g: Number.parseInt(hex.slice(2, 4), 16), b: Number.parseInt(hex.slice(4, 6), 16), a: 255 };
	}
	if (/^[0-9a-f]{8}$/.test(hex)) {
		return {
			r: Number.parseInt(hex.slice(0, 2), 16),
			g: Number.parseInt(hex.slice(2, 4), 16),
			b: Number.parseInt(hex.slice(4, 6), 16),
			a: Number.parseInt(hex.slice(6, 8), 16),
		};
	}
	return null;
}

/** Compose RGBA into hex. It uses 6 digits when fully opaque and 8 digits otherwise. */
export function rgbaToHex({ r, g, b, a }: Rgba): string {
	const base = `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
	if (a >= 255) return base;
	return base + toHex2(a);
}

export function rgbToHsv({ r, g, b }: { r: number; g: number; b: number }): { h: number; s: number; v: number } {
	const rn = r / 255;
	const gn = g / 255;
	const bn = b / 255;
	const max = Math.max(rn, gn, bn);
	const min = Math.min(rn, gn, bn);
	const d = max - min;
	let h = 0;
	if (d !== 0) {
		if (max === rn) h = ((gn - bn) / d) % 6;
		else if (max === gn) h = (bn - rn) / d + 2;
		else h = (rn - gn) / d + 4;
		h *= 60;
		if (h < 0) h += 360;
	}
	const s = max === 0 ? 0 : d / max;
	return { h, s: s * 100, v: max * 100 };
}

export function hsvToRgb({ h, s, v }: { h: number; s: number; v: number }): { r: number; g: number; b: number } {
	const sn = s / 100;
	const vn = v / 100;
	const c = vn * sn;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = vn - c;
	let rp;
	let gp;
	let bp;
	if (h < 60) [rp, gp, bp] = [c, x, 0];
	else if (h < 120) [rp, gp, bp] = [x, c, 0];
	else if (h < 180) [rp, gp, bp] = [0, c, x];
	else if (h < 240) [rp, gp, bp] = [0, x, c];
	else if (h < 300) [rp, gp, bp] = [x, 0, c];
	else [rp, gp, bp] = [c, 0, x];
	return { r: Math.round((rp + m) * 255), g: Math.round((gp + m) * 255), b: Math.round((bp + m) * 255) };
}

/** Parse any supported string into HSVA. Falls back to opaque black. */
export function parseToHsva(input: string | undefined): Hsva {
	const rgba = parseToRgba(input);
	if (!rgba) return { ...DEFAULT_HSVA };
	return { ...rgbToHsv(rgba), a: rgba.a };
}

/** Compose HSVA into the stored hex string. */
export function hsvaToHex(hsva: Hsva): string {
	return rgbaToHex({ ...hsvToRgb(hsva), a: hsva.a });
}

/**
 * Normalize a user-typed hex into the canonical stored form. It tolerates a missing `#`,
 * upper or lower case, surrounding whitespace, and the 3-digit shorthand. It returns
 * `#rrggbb` when opaque and, only when alpha is allowed, `#rrggbbaa`. An opaque field drops
 * any alpha rather than rejecting it. Returns null when the text is not a complete color yet.
 */
export function normalizeHex(input: string | undefined, allowAlpha = false): string | null {
	const rgba = parseToRgba(input);
	if (!rgba) return null;
	return rgbaToHex(allowAlpha ? rgba : { ...rgba, a: 255 });
}
